// Ein Foto fürs PDF der Unterlage (unterlage-pdf.ts, seit dem Augentest E3b,
// 23.10.2026): im Browser auf einer Leinwand das Foto und darüber die
// Zeichnung, dann als JPEG. Die Zeichnung kommt aus denselben Formen, Farben
// und Stärken wie das SVG über dem Foto am Bildschirm (Kern, zeichnung.ts:
// erst der dunkle Rand unter allen Formen, dann die Farben in der Reihenfolge
// der Striche) – so sitzt sie im PDF genau wie im CRM und am iPhone.
//
// Was der Browser dafür liefert (Bild, Leinwand), kommt herein – im Test
// Fälschungen. Bild und Leinwand werden immer gleich wieder freigegeben
// (wie bei der Druckfassung, druckfassung.ts; Safari begrenzt den Speicher
// aller Leinwände zusammen).

import { STRICH_FARBWERTE, ZEICHEN_RAND, zeichnungFormen, type ZeichenForm, type Zeichnung } from "@happy-energy/kern";
import type { Masse } from "./unterlage-pdf.ts";

/** Der Teil des 2D-Zeichenkontexts, den eine Zeichnung braucht. */
export interface ZeichenStift {
  lineCap: string;
  lineJoin: string;
  lineWidth: number;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  fillStyle: string | CanvasGradient | CanvasPattern;
  globalAlpha: number;
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, radius: number, start: number, ende: number): void;
  ellipse(x: number, y: number, rx: number, ry: number, drehung: number, start: number, ende: number): void;
  stroke(): void;
  fill(): void;
}

/** Eine Form der Zeichnung mit dieser Strichbreite – ein Punkt (Linie ohne Länge) als Tupfen. */
function formMalen(stift: ZeichenStift, f: ZeichenForm, breite: number): void {
  stift.lineWidth = breite;
  stift.beginPath();
  switch (f.art) {
    case "linie":
      if (f.x1 === f.x2 && f.y1 === f.y2) {
        stift.arc(f.x1, f.y1, breite / 2, 0, 2 * Math.PI);
        stift.fill();
        return;
      }
      stift.moveTo(f.x1, f.y1);
      stift.lineTo(f.x2, f.y2);
      break;
    case "polylinie": {
      const [erste, ...rest] = f.punkte;
      if (!erste) return;
      stift.moveTo(erste[0], erste[1]);
      for (const [x, y] of rest) stift.lineTo(x, y);
      break;
    }
    case "ellipse":
      stift.ellipse(f.cx, f.cy, Math.max(0, f.rx), Math.max(0, f.ry), 0, 0, 2 * Math.PI);
      break;
  }
  stift.stroke();
}

/** Die Zeichnung auf ein Bild dieser Größe malen – wie `zeichnungSvgInhalt`, nur auf der Leinwand. */
export function zeichnungMalen(stift: ZeichenStift, z: Zeichnung, breite: number, hoehe: number): void {
  const formen = zeichnungFormen(z, breite, hoehe);
  if (formen.length === 0) return;
  stift.save();
  stift.lineCap = "round";
  stift.lineJoin = "round";
  stift.globalAlpha = ZEICHEN_RAND.deckkraft;
  stift.strokeStyle = ZEICHEN_RAND.farbe;
  stift.fillStyle = ZEICHEN_RAND.farbe;
  for (const f of formen) formMalen(stift, f, f.breite * ZEICHEN_RAND.faktor);
  stift.globalAlpha = 1;
  for (const f of formen) {
    stift.strokeStyle = STRICH_FARBWERTE[f.farbe];
    stift.fillStyle = STRICH_FARBWERTE[f.farbe];
    formMalen(stift, f, f.breite);
  }
  stift.restore();
}

/** Ein dekodiertes Foto – im Browser ein ImageBitmap. */
export interface PdfBild {
  close(): void;
}

/** Eine Leinwand – im Browser ein <canvas>. */
export interface PdfLeinwand<B> {
  width: number;
  height: number;
  getContext(art: "2d"): (ZeichenStift & { drawImage(bild: B, x: number, y: number, breite: number, hoehe: number): void; imageSmoothingQuality?: string }) | null;
  toBlob(fertig: (blob: Blob | null) => void, art: string, guete: number): void;
}

export interface PdfFotoWerkzeug<B extends PdfBild> {
  bildAus(roh: Blob): Promise<B>;
  leinwand(): PdfLeinwand<B>;
}

/** Wie fein das JPEG wird – dieselbe Güte wie die Druckfassung. */
export const PDF_JPEG_GUETE = 0.82;

/**
 * Das Foto in dieser Größe (Pixel) samt Zeichnung als JPEG – null, wenn der
 * Browser keine Leinwand oder kein Ergebnis liefert. Ein Foto ohne Zeichnung
 * bleibt ohne. Fehler beim Dekodieren gehen weiter; der Aufrufer setzt dann
 * den Satz „Foto lässt sich nicht laden“.
 */
export async function fotoMitZeichnungJpeg<B extends PdfBild>(roh: Blob, zeichnung: Zeichnung | null, pixel: Masse, werkzeug: PdfFotoWerkzeug<B>): Promise<Uint8Array | null> {
  const bild = await werkzeug.bildAus(roh);
  let leinwand: PdfLeinwand<B> | null = null;
  try {
    const l = (leinwand = werkzeug.leinwand());
    l.width = pixel.breite;
    l.height = pixel.hoehe;
    const stift = l.getContext("2d");
    if (!stift) return null;
    if ("imageSmoothingQuality" in stift) stift.imageSmoothingQuality = "high";
    stift.drawImage(bild, 0, 0, l.width, l.height);
    if (zeichnung && zeichnung.striche.length > 0) zeichnungMalen(stift, zeichnung, l.width, l.height);
    const blob = await new Promise<Blob | null>((fertig) => l.toBlob(fertig, "image/jpeg", PDF_JPEG_GUETE));
    return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
  } finally {
    bild.close();
    if (leinwand) {
      leinwand.width = 0;
      leinwand.height = 0;
    }
  }
}
